import type { IClient } from './clients.js';
import { matchClient, normalizeName } from './clients.js';
import type { IExtraction } from './decide.js';
import { docTypeOf, type IPack, type IPackDocType } from '../packs/index.js';

/**
 * Rules that make the Detail part of a name the same from one run to the next, whatever words the
 * AI picked: a GST/HST period is Q1–Q4 or Annual, a letter is named after its sender. When the AI
 * left the Detail out, some types find it from what it did read: the person an ID record verifies,
 * the other party to an agreement. Which types use which rule is pack data
 * (IPackDocType.detailRule and detailFrom).
 */

const MONTHS: { [name: string]: number } = {
  jan: 1, january: 1, janvier: 1, feb: 2, february: 2, fevrier: 2, mar: 3, march: 3, mars: 3, apr: 4, april: 4, avril: 4,
  may: 5, mai: 5, jun: 6, june: 6, juin: 6, jul: 7, july: 7, juillet: 7, aug: 8, august: 8, aout: 8,
  sep: 9, sept: 9, september: 9, septembre: 9, oct: 10, october: 10, octobre: 10, nov: 11, november: 11, novembre: 11,
  dec: 12, december: 12, decembre: 12
};

interface IMonth {
  year?: number;
  month: number;
}

/** The months of the dates written in a text, in order: "2025-04-01", "April 1, 2025", "1er avril 2025", "Apr 1". */
function monthsIn(text: string): IMonth[] {
  const plain: string = text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const found: { at: number; m: IMonth }[] = [];
  for (const hit of plain.matchAll(/\b(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})\b/g)) {
    found.push({ at: hit.index!, m: { year: Number(hit[1]), month: Number(hit[2]) } });
  }
  // A month written as a word, with the day before or after it and the year when it's there.
  for (const hit of plain.matchAll(/\b(?:\d{1,2}(?:er)?\s+)?([a-z]+)\.?(?:\s+\d{1,2})?(?:,?\s+(\d{4}))?\b/g)) {
    const month: number | undefined = MONTHS[hit[1]];
    if (month) {
      found.push({ at: hit.index!, m: { year: hit[2] ? Number(hit[2]) : undefined, month } });
    }
  }
  return found.filter(f => f.m.month >= 1 && f.m.month <= 12).sort((a, b) => a.at - b.at).map(f => f.m);
}

/**
 * A GST/HST reporting period as Q1–Q4 or Annual: "2025-04-01 to 2025-06-30" is Q2, "January 1 to
 * December 31, 2024" is Annual, "Quarter 3" is Q3. A period that isn't a calendar quarter or a
 * year (a monthly filer, a fiscal quarter) is kept as read.
 */
export function taxPeriod(detail: string | undefined): string | undefined {
  const d: string = (detail ?? '').trim();
  if (!d) {
    return undefined;
  }
  const words: string = d.toLowerCase();
  const quarter: RegExpMatchArray | null = words.match(/^(?:q|quarter\s*|trimestre\s*)([1-4])\b/) ?? words.match(/^([1-4])(?:st|nd|rd|th|e|er)?\s*(?:quarter|trimestre)\b/);
  if (quarter) {
    return `Q${quarter[1]}`;
  }
  if (/^(annual|annuel(le)?|yearly|year)\b/.test(words)) {
    return 'Annual';
  }
  const months: IMonth[] = monthsIn(d);
  if (months.length < 2) {
    return d;
  }
  const from: IMonth = months[0];
  const to: IMonth = months[months.length - 1];
  // "Apr 1 to Jun 30, 2025": the year written once is the year of both.
  const toYear: number | undefined = to.year ?? from.year;
  const fromYear: number | undefined = from.year ?? toYear;
  if (fromYear === undefined || toYear === undefined) {
    return d;
  }
  const length: number = (toYear * 12 + to.month) - (fromYear * 12 + from.month) + 1;
  if (length === 12) {
    return 'Annual';
  }
  if (length === 3 && (from.month - 1) % 3 === 0) {
    return `Q${(from.month + 2) / 3}`;
  }
  return d;
}

/**
 * Whether a matter has a single applicant, and so whose a document is when the AI didn't say:
 * the matter's name, when it is a person's ("Haddad, Rami") and no other person is on the matter.
 * Other ways of writing the same name ("Rami HADDAD", "R. Haddad") are not other people;
 * "Haddad, Lina", the spouse, is.
 */
export function soleApplicant(matter: IClient): string | undefined {
  if (matter.name.indexOf(',') === -1) {
    return undefined;
  }
  const words: string[] = normalizeName(matter.name).split(' ').filter(w => w);
  const samePerson = (alias: string): boolean => {
    const aw: string[] = normalizeName(alias).split(' ').filter(w => w);
    return aw.length > 0 && aw.every(w => words.includes(w) || (w.length === 1 && words.some(n => n.startsWith(w)))) &&
      words.every(n => aw.some(w => w === n || (w.length === 1 && n.startsWith(w))));
  };
  // A matter number or short name written as an alias names nobody.
  const people: string[] = (matter.aliases ?? []).filter(a => /[a-z]{2}/i.test(a) && a !== matter.short && a !== matter.number);
  return people.every(samePerson) ? matter.name.trim() : undefined;
}

/** Words that make a name a business or an estate rather than a person. */
const BUSINESS: RegExp = /\b(inc|incorporated|ltd|limited|llp|llc|corp|corporation|company|co|holdings|group|estate|trust|bank|society|association|ltee|ltée)\b/i;

/**
 * A person's last name: "Linh Nguyen", "LINH NGUYEN" and "Nguyen, Linh" are all Nguyen. None for a
 * matter number or a business ("24-0188", "Harbourview Holdings Inc.").
 */
export function lastName(name: string | undefined): string | undefined {
  const n: string = (name ?? '').trim();
  if (!n || /\d/.test(n) || BUSINESS.test(n)) {
    return undefined;
  }
  const last: string = n.indexOf(',') !== -1 ? n.split(',')[0].trim() : n.split(/\s+/).pop()!;
  if (!/^\p{L}[\p{L}'’-]*$/u.test(last)) {
    return undefined;
  }
  // Written in capitals on an ID or a form: NGUYEN is Nguyen.
  return last === last.toUpperCase() ? last.charAt(0) + last.slice(1).toLowerCase() : last;
}

/**
 * The two parties an agreement is made between, as written: "Between Harbourview Holdings Inc. (the
 * "Purchaser") and Jean Tremblay (the "Vendor")" on one line, or a "BETWEEN:" line with the parties
 * on the lines around "- and -". Roles in brackets and quotes are left out. None when not found.
 */
export function partiesInText(text: string): string[] {
  const clean = (p: string): string => p
    .replace(/\([^)]*\)/g, ' ').replace(/["“”]/g, ' ')
    .split(/,\s|;/)[0]
    .replace(/\s+/g, ' ').replace(/^[\s:–—-]+|[\s:,.–—-]+$/g, '').trim();
  const name = (p: string): boolean => !!p && p.length <= 80 && p.split(' ').length <= 8;
  const lines: string[] = text.split(/\r?\n/).map(l => l.trim());
  for (const line of lines) {
    const m: RegExpMatchArray | null = line.match(/\bbetween\s*:?\s+(.+?)\s+and\s+(.+)$/i);
    const parties: string[] = m ? [clean(m[1]), clean(m[2])] : [];
    if (parties.length === 2 && parties.every(name)) {
      return parties;
    }
  }
  // Court style: BETWEEN: / first party / (role) / - and - / second party / (role)
  const named: string[] = lines.filter(l => l && !/^\(.*\)$/.test(l));
  const at: number = named.findIndex(l => /^between\s*:?$/i.test(l));
  if (at !== -1 && /^[\s–—-]*and[\s:–—-]*$/i.test(named[at + 2] ?? '')) {
    const parties: string[] = [clean(named[at + 1]), clean(named[at + 3] ?? '')];
    if (parties.every(name)) {
      return parties;
    }
  }
  return [];
}

/**
 * The Detail the AI left empty, for the types whose pack says where it comes from: the last name of
 * the person an ID record verifies (the client as the AI wrote it, or the matter's person), or the
 * party to an agreement that isn't the client. A Detail the AI gave is never changed, and nothing is
 * filled when it can't be told which party is the client.
 */
export function fillDetail(x: IExtraction, text: string, clients: IClient[], pack: IPack): IExtraction {
  const type: IPackDocType | undefined = docTypeOf(pack, x.docType);
  if (!type?.detailFrom || x.detail?.trim()) {
    return x;
  }
  const match = matchClient(x.client, clients);
  const matter: IClient | undefined = match && match.score >= 0.8 ? match.client : undefined;
  let detail: string | undefined;
  if (type.detailFrom === 'person') {
    detail = lastName(x.client) ?? (matter ? lastName(matter.name) : undefined);
  } else {
    const isClient = (party: string): boolean => (!!matter && (matchClient(party, [matter])?.score ?? 0) >= 0.8)
      || (!!x.client && normalizeName(party) === normalizeName(x.client));
    const parties: string[] = partiesInText(text);
    const others: string[] = parties.filter(p => !isClient(p));
    detail = parties.length === 2 && others.length === 1 ? others[0] : undefined;
  }
  return detail ? { ...x, detail } : x;
}
